import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, USER_SELECTABLE_LANGUAGES } from '../types/language';
import { userService } from '../services/userService';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(Language.PL_ONLY);
  const [isLoading, setIsLoading] = useState(true);

  // Автоматическое определение языка браузера
  // Возвращает только языки, доступные для выбора пользователем (PL_ONLY или EN)
  const detectBrowserLanguage = (): Language => {
    const browserLang = navigator.language.toLowerCase();
    
    if (browserLang.startsWith('pl')) return Language.PL_ONLY;
    if (browserLang.startsWith('en')) return Language.EN;
    // Русский и другие языки не доступны для выбора пользователем
    // По умолчанию польский
    return Language.PL_ONLY;
  };

  // Проверяет, доступен ли язык для выбора пользователем
  const isSelectableLanguage = (lang: Language): boolean => {
    return USER_SELECTABLE_LANGUAGES.some(opt => opt.code === lang);
  };

  // Загружаем язык пользователя с сервера
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          // Если пользователь авторизован, загружаем его язык с сервера
          const userLanguage = await userService.getUserLanguage();
          // Если язык не доступен для выбора пользователем, используем язык браузера
          const validLanguage = isSelectableLanguage(userLanguage) ? userLanguage : detectBrowserLanguage();
          setLanguageState(validLanguage);
        } else {
          // Если не авторизован, используем язык браузера или сохраненный
          const savedLanguage = localStorage.getItem('app_language') as Language;
          const browserLanguage = detectBrowserLanguage();
          // Проверяем, что сохраненный язык доступен для выбора пользователем
          const validSavedLanguage = savedLanguage && Object.values(Language).includes(savedLanguage) && isSelectableLanguage(savedLanguage)
            ? savedLanguage 
            : null;
          const defaultLanguage = validSavedLanguage || browserLanguage;
          setLanguageState(defaultLanguage);
        }
      } catch (error) {
        console.warn('Failed to load user language, using browser language:', error);
        const browserLanguage = detectBrowserLanguage();
        setLanguageState(browserLanguage);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserLanguage();
  }, []);

  const setLanguage = async (newLanguage: Language) => {
    // Проверяем, что язык доступен для выбора пользователем
    if (!isSelectableLanguage(newLanguage)) {
      console.warn(`Language ${newLanguage} is not available for user selection. Falling back to PL_ONLY.`);
      newLanguage = Language.PL_ONLY;
    }
    
    setLanguageState(newLanguage);
    localStorage.setItem('app_language', newLanguage);
    
    // Сохраняем язык на сервере если пользователь авторизован
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await userService.updateUserLanguage(newLanguage);
      }
    } catch (error) {
      console.warn('Failed to save user language to server:', error);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
